import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { BaseModal } from '../../../components/Modal';
import { handleDowload } from '../models';

// Pretty-prints a JSON string with 2-space indentation. Returns the original
// value if it isn't valid JSON.
const prettyPrint = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const tryParseJSON = (value) => {
  if (value === null || value === undefined) return { ok: false };
  if (typeof value !== 'string') return { ok: true, parsed: value };
  try {
    return { ok: true, parsed: JSON.parse(value) };
  } catch {
    return { ok: false };
  }
};

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const QUOTE = String.fromCharCode(34);

const isContainer = (v) => v !== null && typeof v === 'object';

// Returns the string the tree renders for a primitive (without surrounding
// quotes for strings — quotes are added separately so they're not searchable).
const primitiveSearchText = (v) => {
  if (v === null) return 'null';
  return String(v);
};

// Counts matches the tree will emit for the given value & query. Mirrors the
// traversal/order used by render so total === number of <mark> elements.
const countTreeMatches = (value, re) => {
  let total = 0;
  const walk = (v) => {
    if (isContainer(v)) {
      if (Array.isArray(v)) {
        v.forEach((c) => walk(c));
      } else {
        Object.entries(v).forEach(([k, c]) => {
          total += (String(k).match(re) ?? []).length;
          walk(c);
        });
      }
    } else {
      total += (primitiveSearchText(v).match(re) ?? []).length;
    }
  };
  walk(value);
  return total;
};

// Walks the JSON tree until enough matches have been counted to reach
// `targetIdx`, then returns the path of the node containing that match.
// Path syntax matches collapsedPaths keys.
const findMatchPath = (value, re, targetIdx) => {
  let counter = 0;
  let result = null;
  const walk = (v, path) => {
    if (result !== null) return;
    if (isContainer(v)) {
      if (Array.isArray(v)) {
        for (let i = 0; i < v.length; i += 1) {
          if (result !== null) return;
          walk(v[i], `${path}/${i}`);
        }
      } else {
        const entries = Object.entries(v);
        for (const [k, c] of entries) {
          if (result !== null) return;
          const childPath = `${path}/${k}`;
          const keyMatches = (String(k).match(re) ?? []).length;
          if (counter + keyMatches > targetIdx) {
            result = childPath;
            return;
          }
          counter += keyMatches;
          walk(c, childPath);
        }
      }
    } else {
      const m = (primitiveSearchText(v).match(re) ?? []).length;
      if (counter + m > targetIdx) {
        result = path;
        return;
      }
      counter += m;
    }
  };
  walk(value, '');
  return result;
};

// Splits text into alternating non-match/match tokens and renders matches with
// <mark>. `nextMatchIdx` is a mutable counter object shared across the entire
// tree render so every <mark> gets a unique sequential data-match-idx.
const renderTextWithHighlight = (text, query, nextMatchIdx, activeIdx) => {
  if (!query) return text;
  const parts = String(text).split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));
  return parts.map((part, idx) => {
    if (idx % 2 !== 1) return <React.Fragment key={idx}>{part}</React.Fragment>;
    const i = nextMatchIdx.value;
    nextMatchIdx.value += 1;
    const isActive = i === activeIdx;
    return (
      <mark
        key={idx}
        data-match-idx={i}
        style={{
          padding: 0,
          backgroundColor: isActive ? '#ff9500' : '#ffe066',
          color: isActive ? '#fff' : 'inherit',
        }}
      >
        {part}
      </mark>
    );
  });
};

const valueStyle = (value) => {
  if (value === null) return { color: '#999' };
  switch (typeof value) {
    case 'string':
      return { color: '#a31515' };
    case 'number':
      return { color: '#098658' };
    case 'boolean':
      return { color: '#0451a5' };
    default:
      return {};
  }
};

const ancestorsOf = (path) => {
  if (!path) return [];
  const parts = path.split('/');
  const out = [];
  for (let i = 1; i < parts.length; i += 1) {
    out.push(parts.slice(0, i).join('/'));
  }
  return out;
};

const collectContainerPaths = (value, path, out) => {
  if (!isContainer(value)) return;
  out.push(path);
  if (Array.isArray(value)) {
    value.forEach((child, i) => collectContainerPaths(child, `${path}/${i}`, out));
  } else {
    Object.keys(value).forEach((k) => collectContainerPaths(value[k], `${path}/${k}`, out));
  }
};

const PrimitiveValue = ({ value, query, nextMatchIdx, activeMatchIdx }) => {
  const style = valueStyle(value);
  if (typeof value === 'string') {
    return (
      <span style={style}>
        {QUOTE}
        {renderTextWithHighlight(value, query, nextMatchIdx, activeMatchIdx)}
        {QUOTE}
      </span>
    );
  }
  return (
    <span style={style}>
      {renderTextWithHighlight(primitiveSearchText(value), query, nextMatchIdx, activeMatchIdx)}
    </span>
  );
};

const TreeNode = ({
  name,
  value,
  path,
  collapsedPaths,
  onToggle,
  query,
  nextMatchIdx,
  activeMatchIdx,
  isLast,
  showKey,
}) => {
  const container = isContainer(value);
  const collapsed = collapsedPaths.has(path);
  const indent = { marginLeft: 16 };
  const keyEl = showKey ? (
    <>
      <span style={{ color: '#0451a5' }}>
        {QUOTE}
        {renderTextWithHighlight(String(name), query, nextMatchIdx, activeMatchIdx)}
        {QUOTE}
      </span>
      <span>: </span>
    </>
  ) : null;
  const comma = isLast ? '' : ',';

  if (!container) {
    return (
      <div>
        {keyEl}
        <PrimitiveValue
          value={value}
          query={query}
          nextMatchIdx={nextMatchIdx}
          activeMatchIdx={activeMatchIdx}
        />
        {comma}
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries = isArray ? value.map((v, i) => [i, v]) : Object.entries(value);
  const open = isArray ? '[' : '{';
  const close = isArray ? ']' : '}';
  const toggleBtnStyle = {
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    width: 14,
    padding: 0,
    marginRight: 2,
    color: '#666',
    fontSize: 11,
    fontFamily: 'inherit',
  };

  return (
    <div>
      <div>
        <button type="button" style={toggleBtnStyle} onClick={() => onToggle(path)}>
          <i className={`fas fa-chevron-${collapsed ? 'right' : 'down'}`} />
        </button>
        {keyEl}
        <span>{open}</span>
        {collapsed && (
          <>
            <span style={{ color: '#888', fontStyle: 'italic' }}>
              {' '}
              {entries.length} {entries.length === 1 ? 'item' : 'items'}{' '}
            </span>
            <span>{close}</span>
            {comma}
          </>
        )}
      </div>
      {!collapsed && (
        <>
          <div style={indent}>
            {entries.map(([k, v], idx) => (
              <TreeNode
                key={String(k)}
                name={k}
                value={v}
                path={`${path}/${k}`}
                collapsedPaths={collapsedPaths}
                onToggle={onToggle}
                query={query}
                nextMatchIdx={nextMatchIdx}
                activeMatchIdx={activeMatchIdx}
                isLast={idx === entries.length - 1}
                showKey={!isArray}
              />
            ))}
          </div>
          <div>
            <span>{close}</span>
            {comma}
          </div>
        </>
      )}
    </div>
  );
};

export const ModalDisplay = ({ show, onClose, dataString }) => {
  const [query, setQuery] = useState('');
  const [currentMatch, setCurrentMatch] = useState(0);
  const [collapsedPaths, setCollapsedPaths] = useState(() => new Set());
  const containerRef = useRef(null);

  const formatted = useMemo(() => prettyPrint(dataString), [dataString]);
  const { ok: isJSON, parsed } = useMemo(() => tryParseJSON(dataString), [dataString]);

  // Reset state when modal closes or payload changes.
  useEffect(() => {
    if (!show) {
      setQuery('');
      setCurrentMatch(0);
      setCollapsedPaths(new Set());
    }
  }, [show]);
  useEffect(() => {
    setQuery('');
    setCurrentMatch(0);
    setCollapsedPaths(new Set());
  }, [dataString]);

  // Total match count, computed by mirroring tree-render order so the counter
  // always agrees with the number of <mark> elements the tree will produce.
  const totalMatchCount = useMemo(() => {
    if (!query) return 0;
    const re = new RegExp(escapeRegExp(query), 'gi');
    if (isJSON) return countTreeMatches(parsed, re);
    return (formatted.match(re) ?? []).length;
  }, [query, formatted, isJSON, parsed]);

  // When query changes, snap to first match.
  useEffect(() => {
    setCurrentMatch(0);
  }, [query]);

  // Auto-expand ancestors of the active match if it's hidden inside a
  // collapsed container, so it becomes visible in the tree.
  useEffect(() => {
    if (totalMatchCount === 0 || !isJSON || !query) return;
    const re = new RegExp(escapeRegExp(query), 'gi');
    const targetPath = findMatchPath(parsed, re, currentMatch);
    if (targetPath === null) return;
    const ancestors = ancestorsOf(targetPath);
    setCollapsedPaths((prev) => {
      let changed = false;
      const next = new Set(prev);
      ancestors.forEach((a) => {
        if (next.has(a)) {
          next.delete(a);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [currentMatch, totalMatchCount, query, parsed, isJSON]);

  // Scroll the active match into view once the DOM has been updated with any
  // newly expanded ancestors.
  useEffect(() => {
    if (totalMatchCount === 0 || !containerRef.current) return;
    const el = containerRef.current.querySelector(`mark[data-match-idx="${currentMatch}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [collapsedPaths, currentMatch, totalMatchCount]);

  const goPrev = () => {
    if (totalMatchCount === 0) return;
    setCurrentMatch((c) => (c - 1 + totalMatchCount) % totalMatchCount);
  };
  const goNext = () => {
    if (totalMatchCount === 0) return;
    setCurrentMatch((c) => (c + 1) % totalMatchCount);
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || totalMatchCount === 0) return;
    e.preventDefault();
    if (e.shiftKey) goPrev();
    else goNext();
  };

  const handleTogglePath = (path) => {
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleCollapseAll = () => {
    if (!isJSON) return;
    const paths = [];
    collectContainerPaths(parsed, '', paths);
    // Don't collapse the root, so users can still see the first level.
    setCollapsedPaths(new Set(paths.filter((p) => p !== '')));
  };
  const handleExpandAll = () => {
    setCollapsedPaths(new Set());
  };

  // Mutated as the tree renders; each <mark> takes the next index.
  const nextMatchIdx = { value: 0 };

  return (
    <BaseModal
      show={show}
      title="Data Display"
      onClose={onClose}
      closeButton
      buttons={[
        { title: 'Copy to clipboard', onClick: () => navigator.clipboard.writeText(formatted) },
        { title: 'Download JSON', onClick: () => handleDowload(formatted, 'response_data.json') },
      ]}
      size="lg"
    >
      <InputGroup className="mb-2">
        <Form.Control
          size="sm"
          placeholder="Search variable name or value… (Enter / Shift+Enter to navigate)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        {query && (
          <InputGroup.Append>
            <InputGroup.Text style={{ fontSize: 12 }}>
              {totalMatchCount > 0 ? `${currentMatch + 1} / ${totalMatchCount}` : '0 matches'}
            </InputGroup.Text>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={goPrev}
              disabled={totalMatchCount === 0}
              title="Previous match (Shift+Enter)"
            >
              <i className="fas fa-chevron-up" />
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={goNext}
              disabled={totalMatchCount === 0}
              title="Next match (Enter)"
            >
              <i className="fas fa-chevron-down" />
            </Button>
          </InputGroup.Append>
        )}
      </InputGroup>

      {isJSON && (
        <div className="mb-2 d-flex" style={{ gap: 8 }}>
          <Button size="sm" variant="outline-secondary" onClick={handleExpandAll}>
            <i className="fas fa-plus-square mr-1" /> Expand all
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={handleCollapseAll}>
            <i className="fas fa-minus-square mr-1" /> Collapse all
          </Button>
        </div>
      )}

      <div
        ref={containerRef}
        style={{
          maxHeight: '70vh',
          overflowY: 'auto',
          margin: 0,
          fontSize: 13,
          fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          padding: 8,
          background: '#fafafa',
          border: '1px solid #eee',
          borderRadius: 4,
        }}
      >
        {isJSON ? (
          <TreeNode
            value={parsed}
            path=""
            collapsedPaths={collapsedPaths}
            onToggle={handleTogglePath}
            query={query}
            nextMatchIdx={nextMatchIdx}
            activeMatchIdx={currentMatch}
            isLast
            showKey={false}
          />
        ) : (
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {renderTextWithHighlight(formatted, query, nextMatchIdx, currentMatch) || formatted}
          </pre>
        )}
      </div>
    </BaseModal>
  );
};

export default ModalDisplay;
